/**
 * useBookingForm — engine state hook for the V2 booking form.
 *
 * Owns initial-values construction, all the side-effect fetches (sites,
 * bays, picks, FLDs, reservation types), required-field validation and
 * submit. Mirrors the legacy BookingForm.jsx behaviour byte-for-byte.
 *
 * Returns everything the Shell + slots need so the Shell stays presentational.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { useViewAsClient } from "@/lib/viewAsClient";
import {
  createBooking,
  listBaysLite,
  listForkliftDrivers,
  listSitesLite,
  listReservationTypes,
  loadPicklists,
  updateBooking,
  setBookingDeclarations,
  missingRequired,
  answersFromSnapshot,
  listFieldOverrides,
  listFieldGroups,
} from "@/lib/api";
import { DEFAULT_TAB } from "../registry/tabs";
import { authHeaders } from "@/lib/api/_shared";
import { buildInitialValues } from "./hydration";
import { decomposeInSiteTz } from "@/lib/datetime";
import { computeMissing } from "./validation";

export function useBookingForm({ booking, defaultSiteId, defaultBayId, prefill, onSaved, onOpenChange }) {
  const { user, hasRole, assignments } = useAuth();
  const { viewAsClientId } = useViewAsClient();
  const isEdit = !!booking;

  const [values, setValues] = useState(() =>
    buildInitialValues({ booking, defaultSiteId, defaultBayId, prefill })
  );
  const [sites, setSites] = useState([]);
  const [bays,  setBays]  = useState([]);
  const [picks, setPicks] = useState({});
  const [flds,  setFlds]  = useState([]);
  const [reservationTypes, setReservationTypes] = useState([]);
  const [fieldOverrides, setFieldOverrides] = useState([]);
  const [fieldGroups, setFieldGroups] = useState([]);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState(DEFAULT_TAB);

  const set = (k, v) => setValues((vs) => ({ ...vs, [k]: v }));

  // Required-fields validation (union of hardcoded rules + tenant
  // overrides). Body lives in ./validation.js so the hook stays small
  // and the rules are unit-testable in isolation.
  const missing = useMemo(
    () => computeMissing(values, fieldOverrides),
    [values, fieldOverrides]
  );

  const missingByTab = useMemo(() => {
    const by = {};
    for (const x of missing) by[x.tab] = (by[x.tab] || 0) + 1;
    return by;
  }, [missing]);

  // Sites + FLDs scoped to active tenant.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [{ data: s }, { data: f }] = await Promise.all([
        listSitesLite(viewAsClientId || null),
        listForkliftDrivers(),
      ]);
      if (cancelled) return;
      setSites(s || []);
      setFlds(f || []);
    })();
    return () => { cancelled = true; };
  }, [viewAsClientId]);

  // Picklists re-fetch whenever site changes (per-site scoping).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const p = await loadPicklists(values.site_id || null);
      if (!cancelled) setPicks(p || {});
    })();
    return () => { cancelled = true; };
  }, [values.site_id]);

  // Bays re-fetch + filter on-demand for non-admins.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!values.site_id) { if (!cancelled) setBays([]); return; }
      const isAdmin = hasRole(["super_admin", "client_admin", "site_admin", "dock_manager"]);
      const { data } = await listBaysLite(values.site_id, { includeInactive: isAdmin });
      if (cancelled) return;
      setBays(isAdmin ? (data || []) : (data || []).filter((b) => !b.is_on_demand));
    })();
    return () => { cancelled = true; };
  }, [values.site_id, hasRole]);

  // Reservation types per client.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!values.site_id) { if (!cancelled) setReservationTypes([]); return; }
      const site = sites.find((s) => s.id === values.site_id);
      if (!site?.client_id) return;
      const { data } = await listReservationTypes({ clientId: site.client_id });
      if (!cancelled) setReservationTypes(data || []);
    })();
    return () => { cancelled = true; };
  }, [values.site_id, sites]);

  // Per-tenant booking-form overrides + groups (Phase 1 + Phase 3).
  // Fall back through site → booking → viewAsClientId → first assignment.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const site = sites.find((s) => s.id === values.site_id);
      const fallbackClient =
        viewAsClientId ||
        (assignments || []).find((a) => a.client_id)?.client_id ||
        null;
      const clientId = site?.client_id || booking?.client_id || fallbackClient;
      if (!clientId) { if (!cancelled) { setFieldOverrides([]); setFieldGroups([]); } return; }
      const [{ data: ovs }, { data: grs }] = await Promise.all([
        listFieldOverrides(clientId),
        listFieldGroups(clientId),
      ]);
      if (cancelled) return;
      setFieldOverrides(ovs || []);
      setFieldGroups(grs || []);
    })();
    return () => { cancelled = true; };
  }, [values.site_id, sites, booking?.client_id, viewAsClientId, assignments]);

  const selectedBay  = useMemo(() => bays.find((b) => b.id === values.bay_id), [bays, values.bay_id]);
  const selectedSite = useMemo(() => sites.find((s) => s.id === values.site_id), [sites, values.site_id]);

  // Date/time fields are wall-clock in the selected site's timezone. Once the
  // site is known: stamp its tz, and (edit mode) split the stored instants in it.
  const siteTz = selectedSite?.timezone;
  useEffect(() => {
    if (!siteTz) return;
    setValues((vs) => {
      if (vs._booking_tz === siteTz && (vs._tz_hydrated || !booking)) return vs;
      const next = { ...vs, _booking_tz: siteTz };
      if (booking?.scheduled_start && !vs._tz_hydrated) {
        const st = decomposeInSiteTz(booking.scheduled_start, siteTz);
        const en = decomposeInSiteTz(booking.scheduled_end || booking.scheduled_start, siteTz);
        Object.assign(next, { booking_date: st.dateStr, start_time: st.timeStr, end_time: en.timeStr, _tz_hydrated: true });
      }
      return next;
    });
  }, [siteTz, booking]);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setError(null);

    if (!values.site_id) return failWith("Pick a site", "schedule");
    if (!values.bay_id)  return failWith("Pick a bay",  "schedule");
    if (toMin(values.start_time) >= toMin(values.end_time))
      return failWith("End time must be after start time", "schedule");
    if (selectedBay && selectedBay.max_booking_hours) {
      const sm = toMin(values.start_time);
      const em = toMin(values.end_time);
      if ((em - sm) / 60 > selectedBay.max_booking_hours) {
        return failWith(`This bay only allows bookings up to ${selectedBay.max_booking_hours}h`, "schedule");
      }
    }
    if (!isEdit && !values.vehicle_type)
      return failWith("Vehicle type is required", "vehicle");
    if (!isEdit && !values.rego_prime_mover)
      return failWith("Prime mover rego is required", "vehicle");
    // Phase 9.3: TBA bypasses the driver requirement at submit time.
    if (!isEdit && !values.driver_tba && !values.assigned_driver_id)
      return failWith("Driver is required — pick or add one", "driver");
    // Unticked mandatory declarations don't block; they're flagged "to confirm at arrival".
    const unticked = missingRequired(values._declarations, values.declaration_answers);

    setSaving(true);
    let resp;
    try {
      resp = isEdit
        ? await updateBooking(booking.id, values, user.id)
        : await createBooking(values, user.id);
    } catch (ex) {
      setSaving(false);
      setError(ex?.message || String(ex));
      return;
    }
    setSaving(false);
    const { data, error: err } = resp || {};
    const declarationsChanged = !isEdit ||
      JSON.stringify(values.declaration_answers || {}) !== JSON.stringify(answersFromSnapshot(booking?.declarations));
    // Declarations are stored server-side (frozen wording) right after the booking row exists,
    // before the outbox drain below so the confirmation emails include them.
    if (!err && (data?.id || booking?.id) && (values._declarations || []).length && declarationsChanged) {
      const { error: decErr } = await setBookingDeclarations(data?.id || booking.id, values.declaration_answers);
      if (decErr) toast.error(`Declarations not saved: ${decErr.message}`);
    }
    if (err) {
      setError(err.message || JSON.stringify(err));
      // Phase 9.8+ — rule-enforcement errors from migration 097/098 all
      // relate to schedule/blackouts. Auto-jump to Schedule so the
      // RuleViolationBanner sits above the relevant fields.
      if (/operating hours|overnight operating|overlaps a blackout|exceeds the bay maximum|not a multiple of the|must be at least .* minutes long|must be after start/i.test(err.message || "")) {
        setTab("schedule");
      }
      return;
    }

    toast.success(
      isEdit ? "Booking updated" : `Booking created (${data?.reference_number || "ref pending"})`
    );
    if (unticked.length) toast.warning(`To confirm on arrival before site access: ${unticked.join(", ")}`);
    // Fire-and-forget outbox drain — matches legacy behaviour.
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/email/process-outbox`;
      fetch(url, {
        method: "POST",
        headers: await authHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ limit: 10 }),
        keepalive: true,
      }).catch(() => { /* best-effort */ });
    } catch (_e) { /* never block save */ }
    onSaved?.(data);
    onOpenChange?.(false);
  };

  function failWith(msg, jumpTab) { setError(msg); if (jumpTab) setTab(jumpTab); }
  function toMin(t) { const [h, m] = t.split(":").map(Number); return h * 60 + m; }

  return {
    // state
    values, setValues, set, tab, setTab, error, saving,
    isEdit, user,
    // fetched lists
    sites, bays, picks, flds, reservationTypes, fieldOverrides, fieldGroups,
    // derived
    selectedSite, selectedBay, missing, missingByTab,
    // Phase 9.5 — role gate for Status tab
    hasRole, assignments,
    // actions
    handleSubmit,
  };
}
